/**
 * Desktop Quick Buy panel for product cards.
 * Colour siblings swap the card product preview; sizes update the ATC variant.
 */
class ProductCardQuickBuy extends HTMLElement {
  /** @type {Array<Record<string, any>>} */
  #products = [];

  /** @type {string | number | null} */
  #activeProductId = null;

  /** @type {string | number | null} */
  #activeVariantId = null;

  connectedCallback() {
    const jsonEl = this.querySelector('[data-quick-buy-json]');
    if (!(jsonEl instanceof HTMLScriptElement)) return;

    try {
      this.#products = JSON.parse(jsonEl.textContent || '[]');
    } catch (error) {
      console.warn('[product-card-quick-buy] Invalid product JSON', error);
      return;
    }

    const current = this.#products.find((product) => product.current) || this.#products[0];
    this.#activeProductId = current?.id ?? null;
    this.#activeVariantId = current?.variants?.find((variant) => variant.available)?.id ?? current?.variants?.[0]?.id ?? null;

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const colourButton = target.closest('[data-product-id].product-card-quick-buy__colour');
    if (colourButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectColour(colourButton.dataset.productId);
      return;
    }

    const sizeButton = target.closest('[data-variant-id].product-card-quick-buy__size');
    if (sizeButton instanceof HTMLElement && !sizeButton.hasAttribute('disabled')) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectSize(sizeButton.dataset.variantId);
    }
  };

  /**
   * @param {string | undefined} productId
   */
  #selectColour(productId) {
    if (!productId) return;
    const product = this.#products.find((entry) => String(entry.id) === String(productId));
    if (!product) return;

    this.#activeProductId = product.id;
    const preferred =
      product.variants?.find((variant) => variant.available) || product.variants?.[0] || null;
    this.#activeVariantId = preferred?.id ?? null;

    this.#paintColourSelection();
    this.#paintSizes(product);
    this.#syncCard(product, preferred);
  }

  /**
   * @param {string | undefined} variantId
   */
  #selectSize(variantId) {
    if (!variantId) return;
    const product = this.#products.find((entry) => String(entry.id) === String(this.#activeProductId));
    if (!product) return;

    const variant = product.variants?.find((entry) => String(entry.id) === String(variantId));
    if (!variant) return;

    this.#activeVariantId = variant.id;
    this.#paintSizeSelection();
    this.#syncVariant(product, variant);
  }

  #paintColourSelection() {
    this.querySelectorAll('.product-card-quick-buy__colour').forEach((button) => {
      const selected = String(button.getAttribute('data-product-id')) === String(this.#activeProductId);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  /**
   * @param {Record<string, any>} product
   */
  #paintSizes(product) {
    const sizes = this.querySelector('[data-quick-buy-sizes]');
    if (!(sizes instanceof HTMLElement)) return;

    sizes.replaceChildren();
    for (const variant of product.variants || []) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-card-quick-buy__size';
      button.setAttribute('role', 'listitem');
      button.dataset.variantId = String(variant.id);
      button.dataset.available = String(Boolean(variant.available));
      button.textContent = variant.size || '';
      if (!variant.available) button.disabled = true;
      if (String(variant.id) === String(this.#activeVariantId)) {
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.setAttribute('aria-pressed', 'false');
      }
      sizes.appendChild(button);
    }
  }

  #paintSizeSelection() {
    this.querySelectorAll('.product-card-quick-buy__size').forEach((button) => {
      const selected = String(button.getAttribute('data-variant-id')) === String(this.#activeVariantId);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null} variant
   */
  #syncCard(product, variant) {
    const card = this.closest('product-card');
    if (!(card instanceof HTMLElement)) return;

    card.dataset.productId = String(product.id);
    this.dataset.productId = String(product.id);

    this.#updateLinks(card, product.url);
    this.#updateImage(card, product);
    this.#updateTitle(card, product.title);
    this.#updateBadge(card, product.badge);
    this.#updateUsps(card, product.usps || []);
    this.#updateColourName(card, product.colour);
    this.#syncVariant(product, variant);

    const formComponent = card.querySelector('product-form-component');
    if (formComponent instanceof HTMLElement) {
      formComponent.setAttribute('data-product-id', String(product.id));
      if (product.url) formComponent.setAttribute('data-product-url', product.url);
    }
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null | undefined} variant
   */
  #syncVariant(product, variant) {
    const card = this.closest('product-card');
    if (!(card instanceof HTMLElement) || !variant) return;

    const variantInput = card.querySelector('input[name="id"][ref="variantId"], input[name="id"][data-product-card-variant]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(variant.id);
      variantInput.disabled = !variant.available;
    }

    const addButton = card.querySelector('.product-card-add__button');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !variant.available;
    }

    this.#updatePrice(card, variant.price || product.price, variant.compareAtPrice || product.compareAtPrice);
  }

  /**
   * @param {HTMLElement} card
   * @param {string} url
   */
  #updateLinks(card, url) {
    if (!url) return;
    card.querySelectorAll('a[href]').forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.closest('product-card-quick-buy')) return;
      try {
        const next = new URL(url, window.location.origin);
        const current = new URL(anchor.href, window.location.origin);
        current.pathname = next.pathname;
        current.search = next.search;
        anchor.href = current.toString();
      } catch {
        anchor.href = url;
      }
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {Record<string, any>} product
   */
  #updateImage(card, product) {
    const sources = Array.isArray(product.images) && product.images.length
      ? product.images
      : product.image
        ? [{ src: product.image, srcset: product.imageSrcset || '', alt: product.title || '' }]
        : [];
    if (!sources.length) return;

    const images = card.querySelectorAll('.card-gallery .product-media__image');
    images.forEach((image, index) => {
      if (!(image instanceof HTMLImageElement)) return;
      const source = sources[Math.min(index, sources.length - 1)];
      if (!source?.src) return;
      // Drop the previous srcset first. Otherwise the browser keeps painting
      // the old colour even after src is replaced.
      image.removeAttribute('srcset');
      image.src = source.src;
      if (source.srcset) image.srcset = source.srcset;
      if (source.alt) image.alt = source.alt;
    });

    card.setAttribute('data-featured-media-url', sources[0].src);

    const slideshow = card.querySelector('slideshow-component');
    const firstSlide = slideshow?.querySelector('slideshow-slide');
    const slideId = firstSlide?.getAttribute('slide-id');
    if (slideId && slideshow && typeof slideshow.select === 'function') {
      slideshow.select({ id: slideId }, undefined, { animate: false });
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} title
   */
  #updateTitle(card, title) {
    if (!title) return;
    const titleEl = card.querySelector('.product-card-title, .product-title, [data-product-card-title], .text-block a, a.product-card__title');
    const candidates = card.querySelectorAll('.product-grid__card .text-block, .product-grid__card h2, .product-grid__card h3, .product-grid__card h4');
    let updated = false;
    candidates.forEach((el) => {
      const link = el.querySelector('a');
      if (link) {
        link.textContent = title;
        updated = true;
      } else if (el.childElementCount === 0) {
        el.textContent = title;
        updated = true;
      }
    });
    if (!updated && titleEl) titleEl.textContent = title;

    const overlay = card.querySelector('a.product-card__link');
    if (overlay instanceof HTMLAnchorElement) {
      overlay.setAttribute('aria-label', title);
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string | null | undefined} badge
   */
  #updateBadge(card, badge) {
    let badgeEl = card.querySelector('.product-badges__badge--custom, .product-badges__badge');
    const gallery = card.querySelector('.card-gallery');
    if (!badge) {
      badgeEl?.closest('.product-badges')?.remove();
      return;
    }

    if (!(badgeEl instanceof HTMLElement)) {
      if (!(gallery instanceof HTMLElement)) return;
      const wrap = document.createElement('div');
      wrap.className = 'product-badges product-badges--top-right';
      badgeEl = document.createElement('div');
      badgeEl.className = 'product-badges__badge product-badges__badge--rectangle product-badges__badge--custom';
      wrap.appendChild(badgeEl);
      gallery.appendChild(wrap);
    }

    badgeEl.textContent = badge;
    badgeEl.classList.add('product-badges__badge--custom');
  }

  /**
   * @param {HTMLElement} card
   * @param {string[]} usps
   */
  #updateUsps(card, usps) {
    const list = card.querySelector('.product-usp-labels');
    if (!(list instanceof HTMLElement)) return;

    list.replaceChildren();
    for (const label of usps) {
      const item = document.createElement('li');
      item.className = 'product-usp-labels__label';
      item.textContent = label;
      list.appendChild(item);
    }
    list.classList.toggle('product-usp-labels--empty', usps.length === 0);
  }

  /**
   * @param {HTMLElement} card
   * @param {string | null | undefined} colour
   */
  #updateColourName(card, colour) {
    const colourEl = card.querySelector('[data-product-card-colour]');
    if (!(colourEl instanceof HTMLElement)) return;
    colourEl.textContent = colour || '';
    colourEl.hidden = !colour;
  }

  /**
   * @param {HTMLElement} card
   * @param {string} price
   * @param {string | null | undefined} compareAt
   */
  #updatePrice(card, price, compareAt) {
    const priceRoot = card.querySelector('[data-product-card-price], product-price .price, product-price');
    const compareEl = card.querySelector('[data-product-card-compare], .compare-at-price');
    const priceEl = card.querySelector('[data-product-card-price-value], .price__regular .price-item--regular, .price .price-item--sale, .price-item--regular');

    if (compareEl instanceof HTMLElement) {
      if (compareAt) {
        compareEl.textContent = compareAt;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (priceEl instanceof HTMLElement && price) {
      priceEl.textContent = price;
    } else if (priceRoot instanceof HTMLElement && price) {
      // Fallback structured markup used by product-card-meta
      const sale = priceRoot.querySelector('[data-product-card-price-value]');
      if (sale) sale.textContent = price;
    }
  }
}

if (!customElements.get('product-card-quick-buy')) {
  customElements.define('product-card-quick-buy', ProductCardQuickBuy);
}
